import React from 'react'
import { compose, lifecycle, withHandlers, withState } from 'recompose'
import { equals, head, is, not } from 'ramda'
import { connect } from 'react-redux'

import toJS from '../../../enhancers/ToJS'
import toReduxFormField from '../../../enhancers/ToReduxFormField'

const enhance = compose(
  toReduxFormField('InputFileSelect'),
  // Connecting the value directly in the component since we know it will be inside a form.
  connect(
    ({ form }, { meta: { form: formName }, input: { name: fieldName } }) => ({
      currentValue: form.getIn([formName, 'values', fieldName]),
    }),
  ),
  toJS,
  // `isMounted` tracks whether any async functionality in the component should still execute
  withState('isUnmounted', 'setIsUnmounted', React.createRef()),
  // `status` helps keep track of
  // whether a user has merely canceled a file dialog
  // with or without selecting any files.
  // After selecting a file, both `onDropAccepted` and `onFileDialogCancel` are called,
  // whereas after canceling the dialog, only the latter is called (and that too, not always).
  // 'armed' | 'selecting' | 'selected'
  withState('status', 'setLocalStatusState', 'armed'),
  /**
   * The following states track the validation and error handling outside redux-form.
   * This can be used if we want to reject invalid files before setting them in the form data.
   * This should provide an additional level of flexibility for displaying file selection errors.
   * In addition to `asyncValidate` and `validate` within redux-form,
   * we can also pass down a `validateFile` handler to this field, which controls these local states.
   */
  withState('isValidating', 'setLocalIsValidatingState', false),
  withState('error', 'setLocalErrorState', null),
  /**
   * The following are small wrappers to accommodate local state updates
   * as well as any container state updates if they are specified.
   * This frees us up to display the error here or outside this component.
   */
  withHandlers({
    /**
     * Wrapper to handle a change in the `isValidating` state
     * @param {boolean} isValidating
     */
    setIsValidating: props => isValidating => {
      if (not(props.isUnmounted.current)) {
        props.setLocalIsValidatingState(isValidating)
      }
      // optionally update container state if a handler is passed down
      if (is(Function, props.handleIsValidating)) {
        props.handleIsValidating(isValidating)
      }
    },
    /**
     * Wrapper to handle a change in the `error` state
     * @param {(string|null)} error
     */
    setError: props => error => {
      if (not(props.isUnmounted.current)) {
        props.setLocalErrorState(error)
      }
      // optionally update the container state if a handler is passed down
      if (is(Function, props.handleError)) {
        props.handleError(error)
      }
    },
    /**
     * Wrapper to handle a change in the user's stage in the upload flow
     * @param {string} status
     */
    setStatus: props => status => {
      if (not(props.isUnmounted.current)) {
        props.setLocalStatusState(status)
      }
      // optionally update the container state if a handler is passed down
      if (is(Function, props.handleStatus)) {
        props.handleStatus(status)
      }
    },
  }),
  /**
   * The following wrappers trigger redux-form methods
   * in addition to any additional local behavior.
   */
  withHandlers({
    handleClick: props => async event => {
      const { drop = {}, setStatus } = props
      setStatus('selecting')
      // reset any prior errors
      props.setError(null)

      // Process custom click handling passed down from above.
      if (is(Function, drop.onClick)) drop.onClick(event)
      // Invoke redux-form internal focus handler
      props.input.onFocus(event)
    },
    /**
     * Handles the 'drop' action in react-dropzone.
     * Get called regardless of whether the file selection was successful or not.
     * @param {File[]} files - valid selected files
     * @param {File[]} rejectedFiles - files rejected via dropzone validator (not using this yet)
     * @param {Event} event - event for click or drag/drop
     */
    handleDropAccepted: props => async (files, event) => {
      const { currentValue, drop = {}, input, setStatus } = props

      setStatus('selected')
      let err = null // by default, no additional validation specified
      // Process any custom drop handling
      if (is(Function, drop.onDropAccepted)) {
        await drop.onDropAccepted(files, event)
      }

      // Starting with single file handling.
      // TODO: enable multiple file selection.
      const file = head(files)

      if (!file) return

      // perform optional validation
      // to show any "pre-validation" error
      // and avoid setting an invalid file in the form
      if (is(Function, props.validateFile)) {
        props.setIsValidating(true)
        err = await props.validateFile(file)
      }

      if (!err) {
        props.setError(null)
        input.onBlur(file, event)
      } else {
        props.setError(err)
        input.onBlur(currentValue, event)
      }

      props.setIsValidating(false)
      setStatus('armed')
    },
    /**
     * Handles any rejected files from the react-dropzone component
     */
    handleDropRejected: props => async (files, event) => {
      const { currentValue, drop = {}, input, setStatus } = props

      setStatus('selected')
      let err = 'Please select a valid file'

      // perform optional validation
      // to show any "pre-validation" error
      // and avoid setting an invalid file in the form
      if (is(Function, drop.handleDropRejected)) {
        props.setIsValidating(true)
        await drop.handleDropRejected(files, event)
      }

      const file = head(files) // currently not supporting multiple file upload

      if (!file) return // should not happen, but handling just in case

      // perform optional validation
      // to show any "pre-validation" error
      // and avoid setting an invalid file in the form
      if (is(Function, props.validateFile)) {
        props.setIsValidating(true)
        err = await props.validateFile(file)
      }

      props.setError(err)
      input.onBlur(currentValue, event)

      props.setIsValidating(false)
      setStatus('armed')
    },
    /**
     * Handle if the user just exits the dialog without selecting any files.
     * Also gets called after handleDrop above. So keeping track of
     */
    handleFileDialogCancel: props => async () => {
      const { drop = {}, status, setStatus } = props

      // only continue with this cancel handler
      // IF the user has not selected any files above
      if (not(equals(status, 'armed'))) {
        setStatus('armed') // reset
        return
      }

      if (is(Function, drop.onFileDialogCancel)) {
        // in case further logic is added to this handler
        // potentially use this method passed down from the parent to return a value
        // and stop propagation, similar to the drop.onDropAccepted above
        await drop.onFileDialogCancel()
      }
    },
  }),
  lifecycle({
    componentDidMount() {
      const { props } = this
      const { isUnmounted } = props

      isUnmounted.current = false

      // Note: we temporarily have to include this patch to guarantee that the window 'focus' event gets handled.
      // It turns out that `onFileDialogCancel` doesn't reliably fire when expected:
      // https://github.com/react-dropzone/react-dropzone/issues/575#issuecomment-372879924
      // but we are depending on it to complete the "cycle" of upload steps above.
      // So in this method, we'll just trigger `handleFileDialogCancel` ourselves.
      // PS: saving this reference to the listener so it can be cleared on unmount below.
      this.windowFocusListener = () => {
        props.handleFileDialogCancel()
      }
      window.addEventListener('focus', this.windowFocusListener)
    },
    componentWillUnmount() {
      const { props } = this
      const { isUnmounted } = props

      isUnmounted.current = true

      window.removeEventListener('focus', this.windowFocusListener)
    },
  }),
)

export default enhance
